import { Locale } from "../types/Locale";

/**
 * The language subtag of a locale code, e.g. "en" for both "en" and "en-GB".
 */
export const languageSubtag = (code: string) =>
  code.toLowerCase().replace("_", "-").split("-")[0];

const normalizeCode = (code: string) => code.toLowerCase().replace("_", "-");

/**
 * The locale whose code matches the given code, ignoring case and
 * underscore/hyphen separator differences.
 */
export const findLocaleByCode = (
  locales: Locale[],
  code?: string | null,
): Locale | undefined => {
  if (!code) {
    return undefined;
  }

  return locales.find(
    (locale) => normalizeCode(locale.code) === normalizeCode(code),
  );
};

/**
 * The browser's preferred languages, most preferred first.
 */
const getBrowserLanguages = () => {
  if (navigator.languages && navigator.languages.length) {
    return [...navigator.languages];
  }

  return navigator.language ? [navigator.language] : [];
};

/**
 * The locale that best matches the browser's language preferences. Each
 * preference is tried as an exact code match first, then relaxed to a language
 * match so that a browser set to "en-AU" still resolves to an English locale.
 */
const findBrowserLocale = (locales: Locale[]): Locale | undefined => {
  const browserLanguages = getBrowserLanguages();

  const exactMatch = browserLanguages
    .map((language) => findLocaleByCode(locales, language))
    .find(Boolean);

  if (exactMatch) {
    return exactMatch;
  }

  return browserLanguages
    .map((language) =>
      locales.find(
        (locale) => languageSubtag(locale.code) === languageSubtag(language),
      ),
    )
    .find(Boolean);
};

/**
 * The locale to show when the learner has not picked one: their browser's
 * language if the TMS supports it, otherwise the TMS default.
 */
export const resolveDefaultLocale = (locales: Locale[]): Locale | undefined =>
  findBrowserLocale(locales) ??
  locales.find((locale) => locale.default) ??
  locales[0];
